import dayjs from 'dayjs';
import CustomerChartSummary from 'models/CustomerChartSummary';

export type SummaryDateField = 'period_start_date';

export const formatSummaryDates = (
  data: CustomerChartSummary[],
  field: SummaryDateField
) => {
  return data.map(item => ({
    ...item,
    [field]: dayjs(item[field]).format('D MMM YY')
  }));
};

export const sortByDateAsc = (
  data: CustomerChartSummary[],
  field: SummaryDateField
) => {
  return [...data].sort((a, b) => {
    const da = dayjs(a[field]);
    const db = dayjs(b[field]);
    if (!da.isValid() && !db.isValid()) return 0;
    if (!da.isValid()) return 1;
    if (!db.isValid()) return -1;
    if (da.isBefore(db)) return -1;
    if (da.isAfter(db)) return 1;
    return 0;
  });
};

const getMaxDate = (data: CustomerChartSummary[], field: SummaryDateField) => {
  return data.reduce<dayjs.Dayjs | null>((max, item) => {
    const current = dayjs(item[field]);
    if (!current.isValid()) return max;
    if (!max || current.isAfter(max)) return current;
    return max;
  }, null);
};

export const filterToLastExistingMonths = (
  data: CustomerChartSummary[],
  field: SummaryDateField,
  monthsCount: number
) => {
  const uniqueYearMonthsDesc = Array.from(
    new Set(
      data
        .map(item => dayjs(item[field]))
        .filter(d => d.isValid())
        .map(d => `${d.year()}-${d.month()}`)
    )
  ).sort((a, b) => {
    const [ay, am] = a.split('-').map(Number);
    const [by, bm] = b.split('-').map(Number);
    if (ay === by) return bm - am;
    return by - ay;
  });

  const allowed = new Set(uniqueYearMonthsDesc.slice(0, monthsCount));

  return data.filter(item => {
    const d = dayjs(item[field]);
    if (!d.isValid()) return false;
    const key = `${d.year()}-${d.month()}`;
    return allowed.has(key);
  });
};

export const filterToMonthOfMaxDate = (
  data: CustomerChartSummary[],
  field: SummaryDateField
) => {
  const maxDate = getMaxDate(data, field);
  if (!maxDate) return [];
  return data.filter(item => {
    const d = dayjs(item[field]);
    return d.isValid() && d.year() === maxDate.year() && d.month() === maxDate.month();
  });
};

export const calculateAverage = (data: Array<Record<string, number>>, key: string): string => {
  if (!data || data.length === 0) return '0';
  const total = data.reduce((sum, item) => sum + (item[key] || 0), 0);
  const average = total / data.length;
  return average.toFixed(2);
};
